/**
 * DeepDive mock state — the prototype's `ChatContext` state, moved behind the
 * contract. `creditsSpent` is read by the credits endpoint (mock/index.ts), so
 * spending on an answer moves the sidebar's AI credits.
 *
 * **Nothing here persists.** A reload is the eight seeded chats again, which is
 * the prototype's standing behaviour.
 */
import type { ChatMessage, ChatMode } from '@/data-sources/contract';

import { answerQuestion } from '../engine/answers';
import { SEED_CHATS } from '../engine/chats';
import { allRows } from '../engine/rows';
import { toContractAnswer } from './convert';

export type StoredChat = {
  id: string;
  title: string;
  /** Epoch ms — offsets from `REFERENCE_DATE`, never the real clock. */
  createdAt: number;
  starred: boolean;
  messages: ChatMessage[];
};

/** What the server remembers about an answer so Regenerate can ask it again. */
export type AnswerMeta = { question: string; mode: ChatMode; attempt: number };

type ChatDb = {
  creditsSpent: number;
  /**
   * Sequence for message ids and new chats' timestamps — not a clock: a new
   * chat is stamped at the reference date plus its ordinal, so newest-first
   * sorting holds and it reads "Just now" (the prototype's `seq`).
   */
  seq: number;
  /** Seeded lazily on first read — see `chats()`. */
  seeded: StoredChat[] | null;
  meta: Map<string, AnswerMeta>;
};

export const chatDb: ChatDb = {
  creditsSpent: 0,
  seq: 0,
  seeded: null,
  meta: new Map(),
};

/**
 * A seeded chat arrives answered: the question and its reply, already settled.
 * The reply is computed from the corpus as it stood when the app loaded (no
 * sentiment corrections), which is what a chat from an hour ago would hold —
 * and exactly what the prototype's provider seeded.
 *
 * None starred: the Chat History frame (2059:26344) draws every row `Starred?=False`.
 */
function seed(): StoredChat[] {
  return SEED_CHATS.map((chat) => {
    const answerId = `${chat.id}-a`;
    chatDb.meta.set(answerId, { question: chat.title, mode: chat.mode, attempt: 0 });
    return {
      id: chat.id,
      title: chat.title,
      createdAt: chat.createdAt,
      starred: false,
      messages: [
        { id: `${chat.id}-q`, role: 'user', text: chat.title },
        {
          id: answerId,
          role: 'assistant',
          status: 'done',
          answer: toContractAnswer(answerQuestion(chat.title, chat.mode, allRows)),
          feedback: null,
          mode: chat.mode,
        },
      ],
    };
  });
}

/** Every chat, newest-created first as the prototype held them (new chats are prepended). */
export function chats(): StoredChat[] {
  chatDb.seeded ??= seed();
  return chatDb.seeded;
}

export function findChat(id: string): StoredChat | undefined {
  return chats().find((chat) => chat.id === id);
}

export function nextId(): string {
  chatDb.seq += 1;
  return `m${chatDb.seq}`;
}

/** Back to the seeded state — tests only. */
export function resetChatDb(): void {
  chatDb.creditsSpent = 0;
  chatDb.seq = 0;
  chatDb.seeded = null;
  chatDb.meta.clear();
}
